import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { CheckCircle2, FileText, IdCard, Loader2, LockKeyhole, ShieldCheck } from "lucide-react";
import { useReducedMotion } from "motion/react";
import { api } from "../lib/api";
import { ApiError } from "../lib/api-client/transport";
import type { IdentityDocument, IdentityVerification } from "../lib/api-client/profiles";
import { captureProfileGuard } from "../lib/profileSession";
import { identityDocumentFileError } from "../lib/identityDocumentFiles";
import { Button } from "./beui/Button";
import { Card } from "./beui/SurfaceCard";
import { IdentityDocumentPicker } from "./IdentityDocumentPicker";
import { AttachmentRow } from "./motion/attachment-upload/rows";
import { ImageLightbox } from "./ImageLightbox";

const DOCUMENTS: { type: IdentityDocument; label: string; step: string; hint: string }[] = [
  { type: "id_image", label: "Government ID", step: "Government ID", hint: "A clear image of the ID you selected." },
  { type: "selfie", label: "Selfie holding your ID", step: "Selfie", hint: "Keep your face and ID visible in one photo." },
  { type: "address_proof", label: "Proof of address", step: "Address proof", hint: "A document showing your name and address." },
];
const ID_TYPES = [
  ["national_id", "National ID (PhilSys)"], ["passport", "Passport"],
  ["drivers_license", "Driver's License"], ["umid", "UMID"], ["sss", "SSS ID"],
];
const ID_FORMATS: Record<string, { hint: string; digitsOnly: boolean; max: number; valid: (value: string) => boolean }> = {
  national_id: { hint: "12 digits (PhilSys PCN)", digitsOnly: true, max: 12, valid: value => /^\d{12}$/.test(value) },
  umid: { hint: "12 digits (CRN)", digitsOnly: true, max: 12, valid: value => /^\d{12}$/.test(value) },
  sss: { hint: "10 digits", digitsOnly: true, max: 10, valid: value => /^\d{10}$/.test(value) },
  passport: { hint: "8–9 characters, e.g. P1234567", digitsOnly: false, max: 9, valid: value => /^[A-Z0-9]{8,9}$/.test(value) },
  drivers_license: { hint: "e.g. N01-23-456789", digitsOnly: false, max: 15, valid: value => /^[A-Z0-9-]{10,15}$/.test(value) },
};
const REQUEST_TIMEOUT_MS = 120_000;
type SelectedDocument = { file: File; url: string; status: "uploading" | "failed" | "complete" };

function sanitizeId(type: string, raw: string) {
  const format = ID_FORMATS[type];
  if (!format) return raw.slice(0, 30);
  if (type === "drivers_license") {
    const characters = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
    return [characters.slice(0, 3), characters.slice(3, 5), characters.slice(5)].filter(Boolean).join("-").slice(0, format.max);
  }
  return raw.toUpperCase().replace(format.digitsOnly ? /[^0-9]/g : /[^A-Z0-9-]/g, "").slice(0, format.max);
}

export function IdentityVerificationForm({ verification, onChange }: {
  verification: IdentityVerification;
  onChange: (verification: IdentityVerification) => void;
}) {
  const [idType, setIdType] = useState(verification.idType || "");
  const [idNumber, setIdNumber] = useState("");
  const idInput = useRef<HTMLInputElement>(null);
  const idCursor = useRef<number | null>(null);
  const [detailsEdited, setDetailsEdited] = useState(false);
  const [files, setFiles] = useState<Partial<Record<IdentityDocument, SelectedDocument>>>({});
  const [replacement, setReplacement] = useState<IdentityDocument | null>(null);
  const [replacementRequest, setReplacementRequest] = useState(0);
  const [operation, setOperation] = useState<IdentityDocument | "submit" | null>(null);
  const [selectingPhoto, setSelectingPhoto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ src: string; alt: string } | null>(null);
  const latestVerification = useRef(verification);
  const pending = useRef<AbortController | null>(null);
  const mounted = useRef(false);
  const ownedUrls = useRef(new Set<string>());
  const reduce = useReducedMotion() ?? false;
  const format = ID_FORMATS[idType];
  const validDetails = !!idType && (format ? format.valid(idNumber) : idNumber.trim().length >= 3);
  const detailsReady = !detailsEdited && !!verification.idNumber || validDetails;
  const complete = (doc: IdentityDocument) => verification.docs[doc] && (!files[doc] || files[doc]?.status === "complete");
  const completed = DOCUMENTS.filter(doc => complete(doc.type));
  const active = DOCUMENTS.find(doc => doc.type === replacement) || DOCUMENTS.find(doc => !complete(doc.type));
  const selected = active && files[active.type];
  const busy = operation !== null || selectingPhoto;

  useLayoutEffect(() => {
    if (idCursor.current === null || !idInput.current) return;
    let position = 0;
    let count = 0;
    while (position < idNumber.length && count < idCursor.current) {
      if (idNumber[position] !== "-") count++;
      position++;
    }
    idInput.current.setSelectionRange(position, position);
    idCursor.current = null;
  });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pending.current?.abort();
      for (const url of ownedUrls.current) URL.revokeObjectURL(url);
      ownedUrls.current.clear();
    };
  }, []);

  function acceptVerification(next: IdentityVerification) {
    if (!next?.docs) throw new Error("Could not confirm your verification status. Reload this page before continuing.");
    latestVerification.current = next;
    onChange(next);
    return next;
  }

  async function withRequest(kind: IdentityDocument | "submit", action: (signal: AbortSignal, assertCurrent: () => void) => Promise<void>) {
    if (pending.current) return;
    const controller = new AbortController();
    const assertProfile = captureProfileGuard();
    const assertCurrent = () => { assertProfile(); if (!mounted.current) throw new DOMException("Page closed.", "AbortError"); };
    pending.current = controller;
    setOperation(kind); setError(null);
    const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      assertCurrent();
      await action(controller.signal, assertCurrent);
    } catch (failure) {
      try { assertCurrent(); } catch { return; }
      if (kind !== "submit") setFiles(current => current[kind] ? { ...current, [kind]: { ...current[kind], status: "failed" } } : current);
      setError(controller.signal.aborted ? kind === "submit" ? "The request took too long. Reload to check whether your submission was received." : "The upload took too long. Retry this file."
        : failure instanceof TypeError ? "Could not connect. Check your connection and retry."
        : failure instanceof Error ? failure.message : "Could not complete verification. Try again.");
      if (failure instanceof ApiError && failure.status === 401) setError("Your session expired. Sign in again to continue.");
      if (failure instanceof ApiError && failure.status === 409) setError("Your verification status changed. Reload this page before continuing.");
    } finally {
      window.clearTimeout(timer);
      pending.current = null;
      if (mounted.current) setOperation(null);
    }
  }

  async function prepareDetails(signal: AbortSignal, assertCurrent: () => void) {
    if (!detailsEdited && latestVerification.current.idNumber) return latestVerification.current;
    if (!validDetails) throw new Error("Enter a valid ID type and number before uploading.");
    const saved = await api.startWorkerKyc(idType, idNumber.trim(), signal);
    assertCurrent();
    // A rejected request starts a fresh draft when its ID changes; its old documents must not count toward the new submission.
    const next = acceptVerification(saved.verification);
    setDetailsEdited(false); setIdNumber("");
    return next;
  }

  function upload(doc: IdentityDocument, file: File) {
    if (pending.current || !detailsReady) return;
    const invalid = identityDocumentFileError(file);
    if (invalid) {
      setError(invalid);
      return;
    }
    const url = URL.createObjectURL(file);
    ownedUrls.current.add(url);
    setPreview(null);
    setFiles(current => ({ ...current, [doc]: { file, url, status: "uploading" } }));
    void withRequest(doc, async (signal, assertCurrent) => {
      await prepareDetails(signal, assertCurrent);
      assertCurrent();
      const uploaded = await api.uploadKycDoc(doc, file, signal);
      assertCurrent();
      const next = acceptVerification(uploaded.verification);
      if (!next.docs[doc]) throw new Error("The upload was not confirmed. Retry this file.");
      setFiles(current => ({ ...current, [doc]: { file, url, status: "complete" } }));
      setReplacement(null);
    });
  }

  function submit() {
    if (pending.current || completed.length !== DOCUMENTS.length || !detailsReady) return;
    void withRequest("submit", async (signal, assertCurrent) => {
      const draft = await prepareDetails(signal, assertCurrent);
      if (!DOCUMENTS.every(doc => draft.docs[doc.type])) throw new Error("Your ID changed. Upload the three documents for this ID before submitting.");
      const submitted = await api.submitWorkerKyc(signal);
      assertCurrent();
      acceptVerification(submitted.verification);
    });
  }

  function discardSelection(doc: IdentityDocument) {
    setFiles(current => { const next = { ...current }; delete next[doc]; return next; });
    setReplacement(null); setError(null);
  }

  return <div className="verification-identity-form space-y-4">
    <h2 className="text-lg font-bold text-navy">Identity verification</h2>
    {verification.status === "rejected" && <div className="card p-4 border-l-4 border-brand-red bg-red-50">
      <h3 className="font-semibold text-brand-red">Your previous submission needs changes</h3>
      <p className="text-sm mt-1">{verification.decisionNote}</p>
      <p className="text-xs text-mut mt-1">Update your details or replace the documents, then resubmit.</p>
    </div>}
    <div className="identity-setup">
      <Card className="verification-step identity-details gap-0 p-5">
        <h3 className="font-bold text-navy mb-4 flex items-center gap-2"><IdCard className="w-4 h-4" aria-hidden="true" />Your government ID</h3>
        <label className="label" htmlFor="verification-id-type">ID type</label>
        <select id="verification-id-type" className="field mb-4" value={idType} disabled={busy} onChange={event => {
          setIdType(event.target.value); setIdNumber(""); setDetailsEdited(true);
        }}>
          <option value="" disabled>Select ID type</option>
          {idType && !ID_TYPES.some(([value]) => value === idType) && <option value={idType}>{idType}</option>}
          {ID_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <label className="label" htmlFor="verification-id-number">ID number</label>
        <input ref={idInput} id="verification-id-number" className="field" value={idNumber} disabled={busy || !idType} autoComplete="off"
          inputMode={format?.digitsOnly ? "numeric" : "text"} aria-describedby="verification-id-hint"
          placeholder={!detailsEdited && verification.idNumber ? verification.idNumber : "As printed on your ID"}
          onChange={event => {
            if (idType === "drivers_license") idCursor.current = event.target.value.slice(0, event.target.selectionStart ?? event.target.value.length).replace(/[^a-z0-9]/gi, "").length;
            setIdNumber(sanitizeId(idType, event.target.value)); setDetailsEdited(true);
          }} />
        <p id="verification-id-hint" className="text-xs text-mut mt-2">
          {!detailsEdited && verification.idNumber ? "Leave blank to keep your current ID number." : format?.hint || "Select an ID type first."}
        </p>
      </Card>
      <Card className="verification-step identity-documents gap-0 p-5">
        <div className="flex items-center justify-between gap-3 mb-5">
          <h3 className="font-bold text-navy flex items-center gap-2"><FileText className="w-4 h-4" aria-hidden="true" />Identity documents</h3>
          <span className="text-xs text-mut" aria-live="polite">{completed.length} of 3 uploaded</span>
        </div>
        <ol className="upload-steps" aria-label="Identity document steps">
          {DOCUMENTS.map((doc, index) => <li key={doc.type} aria-current={active?.type === doc.type ? "step" : undefined} className={complete(doc.type) ? "done" : ""}>
            <span>{complete(doc.type) ? <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> : index + 1}</span><strong>{doc.step}</strong>
          </li>)}
        </ol>
        {active ? <section aria-label={active.label}>
          <h4 className="text-lg font-bold text-navy">{active.label}</h4>
          <p className="text-sm text-mut mt-1 mb-4">{active.hint}</p>
          {!detailsReady && <p className="text-sm text-mut mb-3">Enter your ID type and number to enable uploads.</p>}
          {selected?.status === "uploading" ? <p className="identity-uploading" role="status"><Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />Uploading {selected.file.name}…</p> : <>
            {selected?.status === "failed" && <ul className="mb-3"><AttachmentRow
              item={{ id: active.type, name: selected.file.name, kind: selected.file.type.startsWith("image/") ? "image" : "file", size: selected.file.size, previewUrl: selected.url, error: "Upload failed" }}
              playing={false} uploading={false} uploadComplete={false} failed removing={false} arrivalIndex={-1} reduce={reduce}
              onRemove={() => discardSelection(active.type)} onRetry={() => upload(active.type, selected.file)}
              onImagePreview={() => setPreview({ src: selected.url, alt: selected.file.name })} />
            </ul>}
            <IdentityDocumentPicker key={`${active.type}:${replacementRequest}`} label={active.label} disabled={busy || !detailsReady} hasSelection={!!selected} replacing={replacement === active.type}
              onFile={file => upload(active.type, file)} onError={setError} onBusyChange={setSelectingPhoto} />
          </>}
          {replacement && !busy && <Button variant="ghost" className="mt-2" onClick={() => discardSelection(replacement)}>Cancel replacement</Button>}
        </section> : <div className="identity-uploads-finished" role="status"><CheckCircle2 className="w-5 h-5" aria-hidden="true" /><div><h4 className="font-bold">All documents uploaded</h4><p className="text-sm mt-1">Review your details, then submit for review.</p></div></div>}
        {completed.length > 0 && <ul className="identity-completed" aria-label="Uploaded identity documents">
          {completed.map(doc => <li key={doc.type}>
            <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0" aria-hidden="true" />
            <div><strong className="block text-sm">{doc.label}</strong><span className="block truncate text-xs text-mut">{files[doc.type]?.file.name || "Uploaded"}</span></div>
            {files[doc.type]?.file.type.startsWith("image/") && <Button variant="ghost" size="sm" aria-label={`Preview ${doc.label}`} onClick={() => setPreview({ src: files[doc.type]!.url, alt: doc.label })}>Preview</Button>}
            {/* Repeated Replace clicks must reopen the source chooser after dismissal for this same slot. */}
            <Button variant="outline" size="sm" disabled={busy} aria-label={`Replace ${doc.label}`} onClick={() => {
              setReplacement(doc.type); setReplacementRequest(current => current + 1); setError(null);
            }}>Replace</Button>
          </li>)}
        </ul>}
        {error && <p className="identity-error text-sm text-brand-red mt-4" role="alert">{error}</p>}
        <p className="text-xs text-mut flex items-center gap-2 mt-5"><LockKeyhole className="w-3 h-3 shrink-0" aria-hidden="true" />Only you and authorized reviewers can view these documents.</p>
      </Card>
    <Card className="identity-submit flex-col items-stretch gap-4 p-5">
      <div><h3 className="font-bold text-navy">{completed.length === 3 && detailsReady ? "Ready for review" : "Complete your identity submission"}</h3>
        <p className="text-sm text-mut mt-1" id="identity-submit-hint">{!detailsReady ? "Enter your ID type and number, then upload the three documents." : completed.length < 3 ? `Upload ${3 - completed.length} remaining ${completed.length === 2 ? "document" : "documents"} to submit.` : "Submitting sends your ID details and documents to our review team."}</p>
      </div>
      <Button onClick={submit} disabled={busy || !detailsReady || completed.length !== 3} aria-describedby="identity-submit-hint" className="bg-brand-red text-white hover:bg-brand-red/90 shrink-0">
        {operation === "submit" ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <ShieldCheck className="w-4 h-4" aria-hidden="true" />}Submit for review
      </Button>
    </Card>
    </div>
    {preview && <ImageLightbox images={[preview]} initialIndex={0} onClose={() => setPreview(null)} />}
  </div>;
}
